/**
 * StatusBadge — unified status pill for purchase orders, sales orders, and production.
 * Uses a built-in color map covering all known status strings.
 * Pass `colorMap` to override or extend the defaults for a specific domain.
 * Pass `namespace` (e.g. 'purchaseOrders', 'salesOrders', 'production') to translate the display label.
 */
import { useTranslation } from 'react-i18next'

const DEFAULT_COLORS: Record<string, string> = {
  // Purchase & Sales orders
  'Borrador':             'bg-slate-400/15 text-slate-400',
  'Pedido abierto':       'bg-sky-500/15 text-sky-400',
  'Recibido':             'bg-emerald-500/15 text-emerald-500',
  'Entregado':            'bg-emerald-500/15 text-emerald-500',
  'Facturado':            'bg-purple-500/15 text-purple-400',
  'Cancelado':            'bg-red-500/15 text-red-500',
  // Production order states
  'Creado':               'bg-slate-500/15 text-slate-400',
  'Estimado':             'bg-slate-500/15 text-slate-400',
  'Programado':           'bg-amber-500/15 text-amber-500',
  'Liberado':             'bg-emerald-500/15 text-emerald-500',
  'Iniciado':             'bg-sky-500/15 text-sky-400',
  'Reportado terminado':  'bg-purple-500/15 text-purple-400',
  'Terminado':            'bg-slate-400/15 text-slate-500',
}

interface StatusBadgeProps {
  status: string | null | undefined
  dark: boolean
  size?: 'xs' | 'sm' | 'md'
  colorMap?: Record<string, string>
  /** i18n namespace to look up the translated label, e.g. 'purchaseOrders', 'salesOrders', 'production' */
  namespace?: string
}

export function StatusBadge({ status, dark, size = 'sm', colorMap, namespace }: StatusBadgeProps) {
  const { t } = useTranslation()

  if (!status) return <span className={dark ? 'text-slate-600' : 'text-slate-400'}>—</span>

  const map = colorMap ?? DEFAULT_COLORS
  const cls = map[status] ?? (dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600')

  // Translate display label when namespace is provided; fall back to raw status
  const displayLabel = namespace
    ? t(`${namespace}.status.${status}`, { defaultValue: status })
    : status

  const sizeCls =
    size === 'xs' ? 'text-[9px] px-1.5 py-0.5' :
    size === 'md' ? 'text-[11px] px-2 py-0.5'  :
    /* sm */        'text-[10px] px-1.5 py-0.5'

  return (
    <span className={`inline-flex items-center rounded font-semibold leading-none ${sizeCls} ${cls}`}>
      {displayLabel}
    </span>
  )
}
